import React from 'react';
import {
  documentToReactComponents,
  Options as DocumentToReactComponentOptions,
} from '@contentful/rich-text-react-renderer';
import { ContentfulEntry } from './ContentfulEntry';
import {
  ArticleRichTextLayoutBlock,
  ArticleRichTextLayoutBlockStyleProps,
} from './ArticleRichTextLayoutBlock/ArticleRichTextLayoutBlock';
import { Block, BLOCKS, Document, Inline } from '@contentful/rich-text-types';
import { Quote } from '../Quote/Quote';
import { renderListItem } from '@jbhi-fi/components/src/RichText/renderListItem';

export const renderEmbeddedEntry = (node: Block | Inline): React.ReactNode => (
  <ArticleRichTextElement entry={new ContentfulEntry(node)} />
);

export const renderText = (
  node: Block | Inline,
  layoutBlockProps: ArticleRichTextLayoutBlockStyleProps = { paddingTop: 4 }
): React.ReactNode => {
  return (
    <ArticleRichTextLayoutBlock {...layoutBlockProps}>
      {documentToReactComponents(node as Document, {
        renderNode: {
          [BLOCKS.EMBEDDED_ASSET]: renderEmbeddedEntry,
          [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntry,
          [BLOCKS.QUOTE]: (_, children) => <Quote>{children}</Quote>,
          [BLOCKS.LIST_ITEM]: renderListItem,
        },
      })}
    </ArticleRichTextLayoutBlock>
  );
};

export const renderOptions: DocumentToReactComponentOptions = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: renderEmbeddedEntry,
    [BLOCKS.EMBEDDED_ENTRY]: renderEmbeddedEntry,
    [BLOCKS.PARAGRAPH]: node => renderText(node),
    [BLOCKS.UL_LIST]: node => renderText(node, { paddingTop: 3.5 }),
    [BLOCKS.OL_LIST]: node => renderText(node, { paddingTop: 3.5 }),
    [BLOCKS.HR]: node => renderText(node, { paddingTop: { mobile: 7.5, desktop: 10 } }),
    [BLOCKS.HEADING_1]: node => renderText(node, { paddingTop: 10 }),
    [BLOCKS.HEADING_2]: node => renderText(node, { paddingTop: 10 }),
    [BLOCKS.HEADING_3]: node => renderText(node, { paddingTop: 10 }),
    [BLOCKS.HEADING_4]: node => renderText(node),
    [BLOCKS.HEADING_5]: node => renderText(node),
    [BLOCKS.HEADING_6]: node => renderText(node),
    [BLOCKS.QUOTE]: node => renderText(node, { paddingTop: 8.25, paddingBottom: 8.25 }),
  },
};

export type ArticleRichTextProps = {
  document: Document;
  renderOptions?: DocumentToReactComponentOptions;
};

export const ArticleRichText: React.FC<ArticleRichTextProps> = ({
  document,
  renderOptions: renderOptionsProp = {},
}) => {
  const options = {
    ...renderOptions,
    ...renderOptionsProp,
    renderNode: {
      ...renderOptions.renderNode,
      ...(renderOptionsProp?.renderNode ?? {}),
    },
  };

  return <>{documentToReactComponents(document, options)}</>;
};

export type ArticleRichTextElementProps = {
  entry: ContentfulEntry;
};

export const ArticleRichTextElement: React.FC<ArticleRichTextElementProps> = ({ entry }) => {
  if (!entry?.fields) return null;
  return (
    <ArticleRichTextLayoutBlock {...entry.layoutBlockProps}>
      <entry.OutputComponent {...entry.componentProps} />
    </ArticleRichTextLayoutBlock>
  );
};
